import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Mail, Bold, Italic, Underline, Link as LinkIcon, Image as ImageIcon, Send, Loader2, Eye, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getBlastRecipients, sendBlastTest, sendBlast, getBlastHistory, uploadImage } from '../services/api';

const TIER_LABELS = { all: 'All Members', free: 'Free (Unpaid)', bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };

export const EmailBlastPanel = () => {
  const [tier, setTier] = useState('free');
  const [subject, setSubject] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [recipients, setRecipients] = useState(null);
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const editorRef = useRef(null);
  const fileRef = useRef(null);

  const loadRecipients = useCallback(async (t) => {
    try {
      setRecipients(await getBlastRecipients(t));
    } catch { setRecipients(null); }
  }, []);

  const loadHistory = useCallback(async () => {
    try { setHistory(await getBlastHistory()); } catch {}
  }, []);

  useEffect(() => { loadRecipients(tier); }, [tier, loadRecipients]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Poll progress while a blast is sending
  useEffect(() => {
    if (!history.some(h => h.status === 'sending')) return;
    const iv = setInterval(loadHistory, 3000);
    return () => clearInterval(iv);
  }, [history, loadHistory]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (https://...)');
    if (url) exec('createLink', url);
  };

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploadingImg(true);
      const result = await uploadImage(file);
      const url = result.url || result.image_url;
      editorRef.current?.focus();
      document.execCommand('insertHTML', false,
        `<img src="${url}" alt="" style="max-width:100%;border-radius:4px;display:block;margin:12px 0;" />`);
      toast({ title: 'Image Added', description: 'Hosted on your R2 storage — no database load.' });
    } catch {
      toast({ title: 'Upload Failed', description: 'Could not upload image', variant: 'destructive' });
    } finally {
      setUploadingImg(false);
    }
  };

  const getBody = () => editorRef.current?.innerHTML?.trim() || '';
  const bodyIsEmpty = () => !editorRef.current?.innerText?.trim() && !editorRef.current?.querySelector('img');

  const buildPayload = () => ({
    subject,
    body_html: getBody(),
    tier,
    button_text: buttonText.trim() || null,
    button_url: buttonUrl.trim() || null,
  });

  const validate = () => {
    if (!subject.trim()) { toast({ title: 'Missing Subject', variant: 'destructive' }); return false; }
    if (bodyIsEmpty()) { toast({ title: 'Missing Message', description: 'Write your email content first', variant: 'destructive' }); return false; }
    if (buttonText.trim() && !buttonUrl.trim()) { toast({ title: 'Button needs a URL', variant: 'destructive' }); return false; }
    return true;
  };

  const handleTest = async () => {
    if (!validate()) return;
    try {
      setTestSending(true);
      const res = await sendBlastTest(buildPayload());
      toast({ title: 'Test Sent', description: res.message });
    } catch (err) {
      toast({ title: 'Test Failed', description: err.response?.data?.detail || 'Could not send test', variant: 'destructive' });
    } finally { setTestSending(false); }
  };

  const handleSend = async () => {
    try {
      setBlasting(true);
      const res = await sendBlast(buildPayload());
      setConfirmOpen(false);
      setConfirmPhrase('');
      toast({ title: 'Blast Dispatched', description: `Sending to ${res.total} member(s) in the background` });
      setSubject(''); setButtonText(''); setButtonUrl('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      loadHistory();
    } catch (err) {
      toast({ title: 'Blast Failed', description: err.response?.data?.detail || 'Could not send blast', variant: 'destructive' });
    } finally { setBlasting(false); }
  };

  const toolbarBtn = "h-8 w-8 p-0 text-amber-500 hover:bg-amber-900/30";

  return (
    <Card className="bg-gray-900/90 border-2 border-amber-900/30" data-testid="email-blast-panel">
      <CardHeader>
        <CardTitle className="font-headline text-xl text-amber-500 tracking-wider flex items-center gap-2">
          <Mail className="w-5 h-5" />
          EMAIL BLAST
        </CardTitle>
        <CardDescription className="font-typewriter text-gray-400">
          Compose a branded email and send it to a selected tier via Resend. Members who muted announcements are skipped automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="font-headline text-sm text-amber-600">AUDIENCE</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="blast-tier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-amber-900/50">
                {Object.entries(TIER_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {recipients && (
              <p className="text-xs font-typewriter text-gray-400" data-testid="blast-recipient-count">
                <span className="text-amber-400 font-bold">{recipients.eligible}</span> will receive it
                {recipients.opted_out > 0 && <span className="text-gray-500"> · {recipients.opted_out} muted announcements</span>}
              </p>
            )}
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label className="font-headline text-sm text-amber-600">SUBJECT</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Your exclusive discount is here..."
              className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
              data-testid="blast-subject-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="font-headline text-sm text-amber-600">MESSAGE</Label>
          <div className="border border-amber-900/50 rounded-md bg-gray-800 overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-amber-900/30 bg-gray-900/60">
              <Button type="button" variant="ghost" size="sm" className={toolbarBtn} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} data-testid="blast-bold-btn"><Bold className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="sm" className={toolbarBtn} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} data-testid="blast-italic-btn"><Italic className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="sm" className={toolbarBtn} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} data-testid="blast-underline-btn"><Underline className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="sm" className={toolbarBtn} onMouseDown={(e) => { e.preventDefault(); insertLink(); }} data-testid="blast-link-btn"><LinkIcon className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="sm" className={toolbarBtn} onClick={() => fileRef.current?.click()} disabled={uploadingImg} data-testid="blast-image-btn">
                {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={insertImage} />
            </div>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="min-h-[180px] max-h-[400px] overflow-y-auto px-4 py-3 font-typewriter text-sm text-gray-200 focus:outline-none [&_a]:text-amber-500 [&_a]:underline [&_img]:max-w-full"
              data-testid="blast-editor"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="font-headline text-sm text-amber-600">BUTTON TEXT (optional)</Label>
            <Input value={buttonText} onChange={(e) => setButtonText(e.target.value)} placeholder="CLAIM YOUR DISCOUNT" className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="blast-button-text" />
          </div>
          <div className="space-y-2">
            <Label className="font-headline text-sm text-amber-600">BUTTON LINK (optional)</Label>
            <Input value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} placeholder="https://deadlinebydaria.com/tiers" className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="blast-button-url" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider" data-testid="blast-preview-btn">
            <Eye className="w-4 h-4 mr-2" />{showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={testSending} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider" data-testid="blast-test-btn">
            {testSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Send Test to Me
          </Button>
          <Button onClick={() => { if (validate()) setConfirmOpen(true); }} className="bg-red-700 hover:bg-red-800 text-white font-headline tracking-wider ml-auto" data-testid="blast-send-btn">
            <Send className="w-4 h-4 mr-2" />
            Send Blast{recipients ? ` (${recipients.eligible})` : ''}
          </Button>
        </div>

        {showPreview && (
          <div className="border border-amber-900/40 rounded-md p-4 bg-black" data-testid="blast-preview">
            <p className="text-xs font-headline tracking-widest text-amber-600/70 mb-3">EMAIL PREVIEW — {subject || '(no subject)'}</p>
            <div className="max-w-xl mx-auto bg-[#0a0a0a] border border-[#292524] rounded">
              <div className="px-6 py-4 border-b border-[#292524] text-center">
                <span className="text-[#d97706] tracking-[3px] uppercase font-serif text-lg">Deadline by Daria</span>
              </div>
              <div className="px-6 py-5 text-[#d6d3d1] text-sm leading-relaxed font-sans [&_a]:text-amber-500" dangerouslySetInnerHTML={{ __html: getBody() || '<p style="color:#57534e">Your message will appear here…</p>' }} />
              {buttonText.trim() && (
                <div className="px-6 pb-6">
                  <span className="inline-block px-7 py-3 bg-[#d97706] text-black font-bold tracking-wider font-mono text-sm rounded-sm">{buttonText}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-headline tracking-widest text-amber-600/80 mb-2">RECENT BLASTS</p>
            <div className="space-y-1.5" data-testid="blast-history">
              {history.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm bg-gray-800/50 border border-amber-900/20 rounded px-3 py-2">
                  <div className="min-w-0">
                    <span className="font-typewriter text-gray-300 truncate">{b.subject}</span>
                    <span className="text-xs text-gray-500 ml-2">{TIER_LABELS[b.tier] || b.tier} · {new Date(b.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-xs text-gray-400">{b.sent}/{b.total}</span>
                    <Badge className={b.status === 'sending' ? 'bg-amber-900/30 text-amber-400' : 'bg-green-900/30 text-green-500'}>
                      {b.status === 'sending' ? 'SENDING…' : 'SENT'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-gray-900 border-2 border-red-900/50">
          <DialogHeader>
            <DialogTitle className="font-headline text-red-500 tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              CONFIRM EMAIL BLAST
            </DialogTitle>
            <DialogDescription className="font-typewriter text-gray-300 pt-2">
              You are about to email <span className="text-amber-400 font-bold">{recipients?.eligible ?? '?'}</span> real member(s) in the <span className="text-amber-400 font-bold">{TIER_LABELS[tier]}</span> audience.
              <br /><br />
              Subject: <span className="text-gray-100">"{subject}"</span>
              <br /><br />
              This cannot be undone. Have you sent yourself a test first?
            </DialogDescription>
          </DialogHeader>
          {(recipients?.eligible ?? 0) > 500 && (
            <div className="space-y-2">
              <Label className="font-typewriter text-xs text-red-400">Large audience — type SEND to confirm:</Label>
              <Input
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value.toUpperCase())}
                placeholder="SEND"
                className="bg-gray-800 border-red-900/50 font-mono tracking-widest"
                data-testid="blast-confirm-phrase"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setConfirmPhrase(''); }} className="border-gray-700 text-gray-300" data-testid="blast-cancel-btn">Cancel</Button>
            <Button onClick={handleSend} disabled={blasting || ((recipients?.eligible ?? 0) > 500 && confirmPhrase !== 'SEND')} className="bg-red-700 hover:bg-red-800 text-white font-headline tracking-wider" data-testid="blast-confirm-btn">
              {blasting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Yes, Send It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmailBlastPanel;
